import React, { useState } from "react";
import { Check, X, ChevronDown, BadgeCheck, Send, Archive, Trash2 } from "lucide-react";
import { GOLD } from "../constants/theme";
import { adminBulkUpdateDrills, adminBulkSetCoach, adminQuickPublishDrill, adminBulkSetStatus, adminBulkSoftDeleteDrills } from "../utils/communityApi";
import { useCoaches } from "../hooks/useCoaches";
import { bamConfirm, bamToast } from "../shared/dialog";

// Floating bottom-center bar that appears when an admin has selected 1+
// drill cards. Lets them bulk-tag type (Game/On-Air), category, and
// coach attribution without leaving the drill grid.
export function DrillBulkBar({ selected, onClearSelection, onSaved, categories, C, dark }) {
  const [busy, setBusy] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [coachOpen, setCoachOpen] = useState(false);
  const coaches = useCoaches();
  const count = selected.size;

  if (count === 0) return null;

  const ids = Array.from(selected);

  const run = async (patch) => {
    if (busy) return;
    setBusy(true);
    try {
      await adminBulkUpdateDrills(ids, patch);
      onSaved && onSaved();
      onClearSelection && onClearSelection();
    } catch (e) {
      console.error(e);
      bamToast(e?.message || "Bulk update failed", { tone: "error" });
    } finally {
      setBusy(false);
      setCatOpen(false);
      setCoachOpen(false);
    }
  };

  const runSetCoach = async (coachId) => {
    if (busy) return;
    setBusy(true);
    try {
      await adminBulkSetCoach(ids, coachId);
      onSaved && onSaved();
      onClearSelection && onClearSelection();
    } catch (e) {
      console.error(e);
      bamToast(e?.message || "Coach assignment failed", { tone: "error" });
    } finally {
      setBusy(false);
      setCoachOpen(false);
    }
  };

  const runQuickPublish = async () => {
    if (busy) return;
    if (!(await bamConfirm({ title: `Publish ${ids.length} drill${ids.length===1?"":"s"} immediately?`, confirmLabel: "Publish" }))) return;
    setBusy(true);
    try {
      for (const id of ids) {
        await adminQuickPublishDrill(id);
      }
      onSaved && onSaved();
      onClearSelection && onClearSelection();
    } catch (e) {
      console.error(e);
      bamToast(e?.message || "Publish failed", { tone: "error" });
    } finally {
      setBusy(false);
    }
  };

  const runArchive = async () => {
    if (busy) return;
    if (!(await bamConfirm({ title: `Archive ${ids.length} drill${ids.length===1?"":"s"}?`, message: "They'll be hidden from the public library but kept in the DB.", confirmLabel: "Archive", danger: true }))) return;
    setBusy(true);
    try {
      await adminBulkSetStatus(ids, "archived");
      onSaved && onSaved();
      onClearSelection && onClearSelection();
    } catch (e) {
      console.error(e);
      bamToast(e?.message || "Archive failed", { tone: "error" });
    } finally { setBusy(false); }
  };

  const runDelete = async () => {
    if (busy) return;
    if (!(await bamConfirm({ title: `Delete ${ids.length} drill${ids.length===1?"":"s"}?`, message: "This soft-deletes the rows; they can be restored from the DB if needed.", confirmLabel: "Delete", danger: true }))) return;
    setBusy(true);
    try {
      await adminBulkSoftDeleteDrills(ids);
      onSaved && onSaved();
      onClearSelection && onClearSelection();
    } catch (e) {
      console.error(e);
      bamToast(e?.message || "Delete failed", { tone: "error" });
    } finally { setBusy(false); }
  };

  const btn = {
    padding: "9px 14px", borderRadius: 9, fontSize: 12.5, fontWeight: 700,
    border: `1px solid ${C.border}`, background: "transparent", color: C.text,
    cursor: busy ? "default" : "pointer", opacity: busy ? 0.5 : 1,
    display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
  };
  const btnGold = { ...btn, background: GOLD, color: "#111", border: "none", fontWeight: 800, boxShadow: `0 4px 14px ${GOLD}44` };

  return (
    <div style={{
      position: "fixed", bottom: 20, left: "50%", transform: "translateX(-50%)",
      zIndex: 5200, padding: "10px 14px", borderRadius: 14,
      background: dark ? "#0E0E0E" : "#FFFFFF",
      border: `1px solid ${GOLD}55`,
      boxShadow: dark ? "0 16px 50px rgba(0,0,0,0.6), 0 0 0 1px rgba(226,221,159,0.15)" : "0 12px 32px rgba(0,0,0,0.15)",
      display: "flex", alignItems: "center", gap: 10, maxWidth: "calc(100vw - 40px)", flexWrap: "wrap",
    }}>
      <span style={{fontSize:11,fontWeight:800,color:GOLD,letterSpacing:1.2,textTransform:"uppercase",marginRight:2}}>
        {count} selected
      </span>
      <button style={btnGold} disabled={busy} onClick={()=>run({ type: "Game" })}>Mark as Game</button>
      <button style={btnGold} disabled={busy} onClick={()=>run({ type: "On-Air" })}>Mark as On-Air</button>
      <div style={{position:"relative"}}>
        <button style={btn} disabled={busy} onClick={()=>setCatOpen(o=>!o)}>
          Set category <ChevronDown size={13}/>
        </button>
        {catOpen && (
          <div style={{position:"absolute",bottom:"calc(100% + 6px)",left:0,minWidth:200,zIndex:10,
            background:C.cardGradient,border:`1px solid ${C.cardBorder}`,borderRadius:10,overflow:"hidden",
            boxShadow:dark?"0 12px 40px rgba(0,0,0,0.55)":"0 8px 24px rgba(0,0,0,0.12)"}}>
            {(categories || []).map(cat => (
              <div key={cat} onClick={()=>run({ category: cat })}
                style={{padding:"9px 14px",fontSize:13,color:C.text,cursor:"pointer"}}
                onMouseEnter={(e)=>{e.currentTarget.style.background="rgba(226,221,159,0.08)";}}
                onMouseLeave={(e)=>{e.currentTarget.style.background="transparent";}}>
                {cat}
              </div>
            ))}
          </div>
        )}
      </div>
      <div style={{position:"relative"}}>
        <button style={btn} disabled={busy} onClick={()=>setCoachOpen(o=>!o)}>
          Set coach <ChevronDown size={13}/>
        </button>
        {coachOpen && (
          <div style={{position:"absolute",bottom:"calc(100% + 6px)",left:0,minWidth:240,maxHeight:340,overflowY:"auto",zIndex:10,
            background:C.cardGradient,border:`1px solid ${C.cardBorder}`,borderRadius:10,
            boxShadow:dark?"0 12px 40px rgba(0,0,0,0.55)":"0 8px 24px rgba(0,0,0,0.12)"}}>
            {[
              { label: "By Any Means Coaches", list: coaches.staff },
              { label: "Masterclass Coaches",  list: coaches.masterclass },
              { label: "Community Coaches",    list: coaches.community },
            ].map(group => group.list && group.list.length > 0 && (
              <div key={group.label}>
                <div style={{fontSize:9,fontWeight:800,color:GOLD,letterSpacing:1.2,textTransform:"uppercase",padding:"9px 14px 4px"}}>{group.label}</div>
                {group.list.map(c => (
                  <div key={c.id} onClick={()=>runSetCoach(c.id)}
                    style={{display:"flex",alignItems:"center",gap:8,padding:"8px 14px",fontSize:13,color:C.text,cursor:"pointer"}}
                    onMouseEnter={(e)=>{e.currentTarget.style.background="rgba(226,221,159,0.08)";}}
                    onMouseLeave={(e)=>{e.currentTarget.style.background="transparent";}}>
                    <span style={{flex:1}}>{c.name}</span>
                    {c.isVerified && <BadgeCheck size={12} color={GOLD}/>}
                  </div>
                ))}
              </div>
            ))}
            {coaches.loading && (
              <div style={{padding:"10px 14px",fontSize:12,color:C.textDim}}>Loading coaches…</div>
            )}
          </div>
        )}
      </div>
      <button style={btn} disabled={busy} onClick={runQuickPublish}>
        <Send size={13}/> Publish now
      </button>
      <button style={btn} disabled={busy} onClick={runArchive} title="Hide from the public library; reversible">
        <Archive size={13}/> Archive
      </button>
      <button style={{...btn, color:"#F87171", borderColor:"rgba(248,113,113,0.4)"}} disabled={busy} onClick={runDelete} title="Soft-delete; can be restored from the DB">
        <Trash2 size={13}/> Delete
      </button>
      <button style={btn} disabled={busy} onClick={onClearSelection}>
        <X size={13}/> Clear
      </button>
    </div>
  );
}

// Small checkbox overlay on drill cards — shown only in admin mode.
export function AdminSelectOverlay({ selected, onToggle, C }) {
  return (
    <div
      onClick={(e)=>{e.stopPropagation();onToggle();}}
      title={selected ? "Deselect" : "Select"}
      style={{
        position:"absolute", top:8, left:8, zIndex:3,
        width:22, height:22, borderRadius:6,
        border:`1.5px solid ${selected ? GOLD : "rgba(255,255,255,0.45)"}`,
        background: selected ? GOLD : "rgba(0,0,0,0.55)",
        backdropFilter:"blur(4px)",
        display:"flex", alignItems:"center", justifyContent:"center",
        cursor:"pointer", transition:"all .12s",
      }}
    >
      {selected && <Check size={13} color="#111" strokeWidth={3}/>}
    </div>
  );
}

export default DrillBulkBar;
